import React from 'react';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import PlanUsagePanel from '../components/common/PlanUsagePanel';
import { useTenantPath } from '../hooks/useTenantPath';

// Dedicated /plan-usage landing — same PlanUsagePanel body as the Impact
// glance card, plus more context and the "Contact sales / Upgrade" surface.
// This is the natural target for every "Upgrade →" CTA elsewhere in the app.

const PlanUsagePage = () => {
  const tenantPath = useTenantPath();

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <CreditCardIcon className="w-6 h-6 text-indigo-500" />
          Plan &amp; Usage
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Your current tier, this cycle&rsquo;s consumption, and how close you are to plan limits.
        </p>
      </div>

      <PlanUsagePanel />

      <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-2">
          Ready to grow?
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
          Serviti grows with you. Upgrade any time and every conversation, lead and team member you&rsquo;ve set up carries over
          &mdash; no reconfiguration, no downtime.
        </p>
        <div className="flex flex-wrap gap-2">
          <Link
            to={tenantPath('/billing')}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
          >
            Change plan or billing period
          </Link>
          <Link
            to={tenantPath('/license-info')}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
          >
            Plan details
          </Link>
          <a
            href="https://serviti.ai/pricing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
          >
            Compare plans
          </a>
          <a
            href="https://serviti.ai/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
          >
            Contact us
          </a>
        </div>
      </div>

      <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
        AI answers reset at the start of each billing cycle, which runs from your subscription date
        &mdash; not the first of the calendar month.
      </p>
    </div>
  );
};

export default PlanUsagePage;
